import { useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '../ui/card'
import { cn } from '../../lib/utils'
import { useTheoreticalPlan } from '../../hooks/useCapexQueries'
import { CONDITION_META, TRADE_META } from '../../lib/capex-utils'
import type { EquipmentCondition, TheoreticalWorksite, TradeCategory } from './types'
import { EmptyState } from '../ui/empty-state'

interface TheoreticalPlanViewProps {
  org: string
  assetUid: string
}

/**
 * Read-only "theoretical trajectory": the always-live capex projection computed
 * from the asset's equipment (condition + age vs lifespan reference). Rendered
 * as a multi-year calendar — years in columns, equipment in rows grouped by
 * trade — to mirror the editable capex plan grid. It is a reference baseline,
 * not a budget: an editable simulation (the plans listed below) is forked from
 * it and is what enters the submit/approve/archive circuit.
 */
export function TheoreticalPlanView({ org, assetUid }: TheoreticalPlanViewProps) {
  const { t, i18n } = useTranslation('capex')
  const { data, isLoading, error } = useTheoreticalPlan(org, assetUid)

  const worksites = useMemo(() => data?.worksites ?? [], [data])
  const summary = data?.summary

  const currentYear = new Date().getFullYear()
  const horizonStart = summary?.horizon_start ?? currentYear
  const horizonEnd = summary?.horizon_end ?? horizonStart + 10
  const years = useMemo(() => {
    const out: number[] = []
    for (let y = horizonStart; y <= horizonEnd; y++) out.push(y)
    return out
  }, [horizonStart, horizonEnd])

  const currency = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language || 'fr', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }),
    [i18n.language],
  )

  // Group by trade (TRADE_META order); sort equipment within a trade by
  // building then name for stable reading.
  const tradeGroups = useMemo(() => {
    const collator = new Intl.Collator(i18n.language || 'fr')
    const groups = new Map<string, TheoreticalWorksite[]>()
    for (const w of worksites) {
      const trade = w.trade ?? 'structural'
      if (!groups.has(trade)) groups.set(trade, [])
      groups.get(trade)!.push(w)
    }
    return [...groups.entries()]
      .sort(([a], [b]) => (TRADE_META[a as TradeCategory]?.order ?? 99) - (TRADE_META[b as TradeCategory]?.order ?? 99))
      .map(([trade, items]) => ({
        trade,
        items: [...items].sort(
          (x, y) => collator.compare(x.building ?? '', y.building ?? '') || collator.compare(x.name, y.name),
        ),
      }))
  }, [worksites, i18n.language])

  const grandTotal = useMemo(
    () => worksites.reduce((s, w) => s + (w.cost_estimate ?? 0), 0),
    [worksites],
  )
  const hasCosts = grandTotal > 0
  const yearTotals = useMemo(
    () => years.map((y) => worksites.filter((w) => w.year === y).reduce((s, w) => s + (w.cost_estimate ?? 0), 0)),
    [years, worksites],
  )

  return (
    <Card data-testid="theoretical-plan">
      <CardContent className="p-4 space-y-4">
        {isLoading && (
          <div className="flex items-center justify-center h-16">
            <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        )}

        {error && <p className="text-sm text-destructive">{t('theoretical.load_error')}</p>}

        {!isLoading && !error && worksites.length === 0 && (
          <EmptyState
            variant="inline"
            className="py-4"
            title={
              summary && summary.equipment_total === 0
                ? t('theoretical.empty_no_equipment')
                : t('theoretical.empty_no_projection')
            }
            data-testid="theoretical-empty"
          />
        )}

        {/* Multi-year calendar: years in columns, equipment in rows. */}
        {worksites.length > 0 && (
          <div
            className="overflow-x-auto overscroll-x-contain rounded-md border border-border"
            data-testid="theoretical-grid"
          >
            <table className="border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 z-20 bg-background border-b border-r border-border w-72 min-w-72 px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide" />
                  {years.map((year) => (
                    <th
                      key={year}
                      className={cn(
                        'min-w-28 px-2 py-2 text-center text-xs font-medium text-muted-foreground uppercase tracking-wide border-b border-l border-border/60',
                        year === currentYear && 'border-x-2 border-x-primary font-bold text-primary bg-primary/5',
                      )}
                    >
                      {year}
                      {year === currentYear && <span className="ml-1 text-primary">•</span>}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {tradeGroups.map(({ trade, items }) => {
                  const meta = TRADE_META[trade as TradeCategory]
                  return (
                    <FragmentTrade key={trade}>
                      {/* Trade header row */}
                      <tr className="bg-muted/40 border-b border-border" data-testid={`theoretical-trade-${trade}`}>
                        <td className="sticky left-0 z-10 bg-muted/40 border-r border-border px-3 py-2 w-72 min-w-72">
                          <div className="flex items-center gap-2">
                            <span className={cn('w-2 h-4 rounded-full border-l-4 shrink-0', meta?.color ?? 'border-border')} />
                            <span className="text-sm font-semibold text-foreground">{t(`trade.${trade}`, trade)}</span>
                            <span className="text-xs text-muted-foreground">({items.length})</span>
                          </div>
                        </td>
                        {years.map((year) => {
                          const sub = items.filter((w) => w.year === year).reduce((s, w) => s + (w.cost_estimate ?? 0), 0)
                          return (
                            <td
                              key={year}
                              className={cn(
                                'min-w-28 px-2 py-2 text-center text-xs text-muted-foreground tabular-nums border-l border-border/40',
                                year === currentYear && 'bg-primary/5',
                              )}
                            >
                              {sub > 0 ? currency.format(sub) : ''}
                            </td>
                          )
                        })}
                      </tr>

                      {/* One row per equipment */}
                      {items.map((w) => (
                        <tr key={w.asset_uid} className="border-b border-border/60 hover:bg-muted/20">
                          <td className="sticky left-0 z-10 bg-background border-r border-border px-3 py-2 w-72 min-w-72">
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-foreground truncate">{w.name}</span>
                              {w.building && <span className="text-xs text-muted-foreground truncate">{w.building}</span>}
                            </div>
                          </td>
                          {years.map((year) => {
                            const active = w.year === year
                            return (
                              <td
                                key={year}
                                className={cn(
                                  'min-w-28 px-1.5 py-1.5 text-center align-middle border-l border-border/40',
                                  year === currentYear && !active && 'bg-primary/5',
                                  active && cn('border-l-4', meta?.color ?? 'border-primary', 'bg-muted/30'),
                                )}
                              >
                                {active && (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <ConditionBadge condition={w.condition} />
                                    {typeof w.cost_estimate === 'number' && w.cost_estimate > 0 && (
                                      <span className="text-[11px] tabular-nums text-muted-foreground">
                                        {currency.format(w.cost_estimate)}
                                      </span>
                                    )}
                                    {w.source === 'age_inferred' && (
                                      <span
                                        className="text-[10px] text-amber-600 dark:text-amber-500"
                                        title={t('theoretical.source_age_hint')}
                                      >
                                        {t('theoretical.source_age')}
                                      </span>
                                    )}
                                    {/* The year moved because the maintenance
                                        log said so — and the line says which
                                        year it moved from, and why. A budget
                                        figure that cannot be questioned is not
                                        a budget figure. */}
                                    {w.source === 'operational' && (
                                      <span
                                        className="text-[10px] text-info"
                                        data-testid="theoretical-source-operational"
                                        title={[
                                          w.reference_year
                                            ? t('theoretical.source_operational_move', {
                                                from: w.reference_year,
                                                to: w.year,
                                              })
                                            : '',
                                          w.rationale ?? '',
                                        ]
                                          .filter(Boolean)
                                          .join(' — ')}
                                      >
                                        {w.reference_year
                                          ? t('theoretical.source_operational_move', {
                                              from: w.reference_year,
                                              to: w.year,
                                            })
                                          : t('theoretical.source_operational')}
                                      </span>
                                    )}
                                    {(w.included_components?.length ?? 0) > 0 && (
                                      <span
                                        className="text-[10px] text-muted-foreground"
                                        data-testid="theoretical-included-components"
                                        title={w.included_components?.map((c) => c.name).join(', ')}
                                      >
                                        {t('theoretical.included_components', {
                                          names: w.included_components?.map((c) => c.name).join(', '),
                                        })}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </FragmentTrade>
                  )
                })}
              </tbody>

              {hasCosts && (
                <tfoot>
                  <tr className="bg-muted/60 border-t-2 border-border font-medium">
                    <td className="sticky left-0 z-10 bg-muted/60 border-r border-border px-3 py-2 w-72 min-w-72 text-xs uppercase tracking-wide text-muted-foreground">
                      {t('theoretical.total_estimate')}
                    </td>
                    {yearTotals.map((total, i) => (
                      <td
                        key={years[i]}
                        className={cn(
                          'min-w-28 px-2 py-2 text-center text-xs tabular-nums border-l border-border/40',
                          years[i] === currentYear && 'bg-primary/5',
                        )}
                      >
                        {total > 0 ? currency.format(total) : ''}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// Grouping helper: <tbody> can't nest, so a trade's header + rows are emitted
// as a fragment inside the single <tbody>.
function FragmentTrade({ children }: { children: ReactNode }) {
  return <>{children}</>
}

function ConditionBadge({ condition }: { condition: EquipmentCondition }) {
  const { t } = useTranslation('capex')
  const meta = CONDITION_META[condition]
  return (
    <span
      className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap', meta?.color ?? '')}
    >
      {t(`condition.${condition}`, meta?.label ?? String(condition))}
    </span>
  )
}
